import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { Pool } from '../../types/pool-plans.types';
import { useAppQuery } from '../use-app-query.hook';

type Pools = {
  companyId: string;
} & (
  | {
      currentPage: number;
      take: number;
      vestByDate?: Date;
    }
  | { currentPage?: never; take?: never; vestByDate?: never }
);

export const usePools = ({ companyId, currentPage, take, vestByDate }: Pools) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useAppQuery<{ pools: Pool[]; totalPages: number }>({
    queryKey: [QueryKey.GET_POOLS, companyId, { currentPage, take, vestByDate }],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'pools'] },
        {
          queryParams: {
            vestByDate,
            ...(take && currentPage
              ? {
                  take,
                  skip: (currentPage >= 1 ? currentPage - 1 : 0) * take,
                }
              : {}),
          },
        },
      ),
    enabled: !!companyId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_POOLS] });
  }, [queryClient]);

  return {
    ...data,
    pools: data?.pools || [],
    invalidateQuery,
    isLoading,
  };
};
